import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";
import Input from "../components/common/Input";
import Button from "../components/common/Button";
import useAuthStore from "../store/useAuthStore";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  rememberMe: z.boolean().optional(),
});

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      rememberMe: false,
    },
  });

  // Set by the API client when a request came back 401 and the session was cleared.
  useEffect(() => {
    if (sessionStorage.getItem("sessionExpired")) {
      sessionStorage.removeItem("sessionExpired");
      const message = "Your session has expired. Please log in again.";
      setError(message);
      toast.error(message);
    }
  }, []);

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError("");

    try {
      const response = await login({
        email: data.email,
        password: data.password,
      });

      toast.success("Login successful! Welcome back.");

      if (response?.user?.role === "superAdmin") {
        navigate("/admin/dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      if (err.response?.data?.status === "payment_required") {
        const paymentUrl = err.response.data.data?.paymentUrl;
        if (paymentUrl) {
          window.location.href = paymentUrl;
          return;
        }
      }

      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Login failed. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <img
            src="logo.png"
            alt="SentryPrime"
            className="mx-auto h-14 w-auto mb-3"
          />
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-border-light">
          <h2 className="text-2xl font-semibold text-text-dark mb-2">
            Welcome Back
          </h2>
          <p className="text-sm text-text-secondary mb-6">
            Sign in to your account to continue
          </p>

          {error && (
            <div className="mb-4 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Email Address"
              type="email"
              placeholder="you@example.com"
              {...register("email")}
              error={errors.email?.message}
            />

            <div>
              <Input
                label="Password"
                type="password"
                placeholder="Enter your password"
                {...register("password")}
                error={errors.password?.message}
              />
              <div className="flex items-center justify-between mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    {...register("rememberMe")}
                    className="w-4 h-4 text-primary border-border-light rounded focus:ring-primary"
                  />
                  <span className="text-sm text-text-secondary">
                    Remember me
                  </span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full"
              icon="mdi:login"
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <div className="mt-6 border-t border-border-light pt-6">
            <p className="text-sm text-text-secondary text-center mb-4">
              Don't have an account?
            </p>
            <div className="space-y-3">
              <Link to="/plans" className="block">
                <Button
                  variant="primary"
                  className="w-full"
                  type="button"
                  icon="mdi:account-plus"
                  iconPosition="right"
                >
                  Sign up for a new account
                </Button>
              </Link>
              <p className="text-xs text-text-secondary text-center">
                Already have a subscription plan?
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-text-secondary">
          <p>© 2024 SentryPrime. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
